import { 
  User as UserIcon, 
  Settings, 
  LogOut, 
  ShieldCheck, 
  Wallet,
  Loader2,
  Copy,
  ExternalLink,
  Upload
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useMe, useUpdateProfile } from "@/hooks/use-queries";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { useAccount, useDisconnect } from 'wagmi';
import { resolveAvatarUrl } from "@/lib/utils";
import { api } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";

export default function Profile() {
  const { data: user, isLoading } = useMe();
  const updateProfileMutation = useUpdateProfile();
  const [displayName, setDisplayName] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const queryClient = useQueryClient();
  
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName);
    }
  }, [user]);

  const handleSignOut = async () => {
    try {
      const { api } = await import("@/lib/api");
      await api.post("/auth/logout");
    } catch {
      // Proceed to login even if logout API fails
    }
    window.location.href = "/login";
  };

  const handleSave = async () => {
    try {
      await updateProfileMutation.mutateAsync({ displayName });
      toast.success("Profile updated successfully");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update profile");
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setIsUploading(true);
      await api.post("/uploads/user/avatar", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.success("Avatar updated successfully");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to upload avatar");
    } finally {
      setIsUploading(false);
      // Reset input value to allow selecting same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const copyAddress = () => {
    if (address) {
      navigator.clipboard.writeText(address);
      toast.success("Address copied to clipboard");
    }
  };

  if (isLoading) return <ProfileSkeleton />;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold">Profile Settings</h1>
          <p className="text-muted-foreground">Manage your account and preferences.</p>
        </div>
        <Button variant="destructive" size="sm" onClick={handleSignOut} className="shadow-lg shadow-destructive/10 self-start sm:self-auto">
          <LogOut className="mr-2 h-4 w-4" /> Sign Out
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Sidebar Info */}
        <div className="md:col-span-1 space-y-6">
          <Card className="glass-panel text-center border-border/40 bg-background/50 backdrop-blur-xl">
            <CardContent className="pt-8 pb-6 px-6 space-y-4">
              <div className="relative inline-block group cursor-pointer" onClick={handleAvatarClick}>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  className="hidden" 
                  accept="image/png, image/jpeg, image/jpg, image/webp" 
                  onChange={handleFileChange} 
                />
                <Avatar className="h-24 w-24 mx-auto border-4 border-primary/20 shadow-xl shadow-primary/5 transition-all group-hover:border-primary/50 group-hover:opacity-90">
                  <AvatarImage src={resolveAvatarUrl(user?.avatarUrl)} className="object-cover" />
                  <AvatarFallback className="text-2xl bg-primary/10 text-primary font-display font-bold">
                    {user?.displayName.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                {isUploading ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                    <Loader2 className="h-6 w-6 text-white animate-spin" />
                  </div>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <Upload className="h-6 w-6 text-white" />
                  </div>
                )}
                <div className="absolute bottom-1 right-1 h-5 w-5 rounded-full bg-green-500 border-2 border-background shadow-glow-sm" />
              </div>
              <div>
                <h2 className="text-xl font-bold font-display">{user?.displayName}</h2>
                <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mt-1">ID: {user?.id.substring(0, 8)}</p>
              </div>
              <div className="flex justify-center flex-wrap gap-2">
                {user?.emailVerified && (
                  <Badge variant="outline" className="bg-primary/5 border-primary/20 text-primary text-[10px] uppercase tracking-wide">
                    <ShieldCheck className="mr-1 h-3 w-3" /> Verified
                  </Badge>
                )}
                {user?.roles?.map((role: string) => (
                  <Badge key={role} variant="secondary" className="text-[10px] uppercase tracking-wide">{role}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-panel border-border/40 bg-background/50 backdrop-blur-xl overflow-hidden">
            <CardHeader className="pb-3 border-b border-border/40 bg-muted/30">
              <CardTitle className="text-xs font-mono uppercase tracking-widest flex items-center gap-2">
                <Wallet className="h-3.5 w-3.5 text-primary" /> Web3 Wallet
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              {isConnected && address ? (
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-primary/5 border border-primary/10 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono uppercase text-muted-foreground">Connected Address</span>
                      <Badge className="bg-green-500/20 text-green-500 border-none h-4 text-[9px]">Active</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs truncate text-foreground/80">{address}</span>
                      <button onClick={copyAddress} className="text-muted-foreground hover:text-primary transition-colors shrink-0">
                        <Copy className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="xs" className="text-[10px] h-8" onClick={() => window.open(`https://amoy.polygonscan.com/address/${address}`, '_blank')}>
                      <ExternalLink className="mr-1 h-3 w-3" /> Scan
                    </Button>
                    <Button variant="ghost" size="xs" className="text-[10px] h-8 text-destructive hover:bg-destructive/10" onClick={() => disconnect()}>
                      Disconnect
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 py-2">
                  <div className="text-center space-y-2">
                    <div className="h-10 w-10 bg-muted/50 rounded-full flex items-center justify-center mx-auto">
                      <Wallet className="h-5 w-5 text-muted-foreground/50" />
                    </div>
                    <p className="text-xs text-muted-foreground">Connect your wallet to receive payments and manage escrows.</p>
                  </div>
                  {/* Web3Modal Button */}
                  <div className="flex justify-center">
                    <w3m-button balance="hide" size="sm" label="Connect Wallet" />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Form */}
        <div className="md:col-span-2 space-y-6">
          <Card className="glass-panel border-border/40 bg-background/50 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="font-display">Personal Information</CardTitle>
              <CardDescription>
                Update your display name and contact details.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="name" className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Display Name</Label>
                <Input 
                  id="name" 
                  value={displayName} 
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="bg-background/50 border-border/40 focus:ring-primary/20" 
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Email Address</Label>
                <Input id="email" value={user?.email || "No email"} disabled className="bg-muted/30 border-border/20 opacity-70 cursor-not-allowed font-mono text-xs" />
                <p className="text-[10px] text-muted-foreground/60 italic">Email is managed via your Google authentication provider.</p>
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={handleSave} disabled={updateProfileMutation.isPending} className="shadow-lg shadow-primary/20">
                  {updateProfileMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-panel border-destructive/20 bg-destructive/5 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="text-destructive font-display">Danger Zone</CardTitle>
              <CardDescription className="text-destructive/70">
                Critical actions for your account security.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg bg-background/40 border border-destructive/10">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Session Access</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Revoke current Google authentication session</p>
                </div>
                <Button variant="outline" size="sm" className="border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors self-start sm:self-auto" disabled>Revoke Session</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="space-y-6 max-w-4xl mx-auto animate-pulse">
      <Skeleton className="h-20 w-full rounded-xl" />
      <div className="grid gap-6 md:grid-cols-3">
        <Skeleton className="h-80 rounded-xl" />
        <Skeleton className="md:col-span-2 h-[500px] rounded-xl" />
      </div>
    </div>
  );
}